import { useCallback, useRef, useState } from "react";
import {
  LiveAvatarSession,
  SessionEvent,
  SessionState,
  ConnectionQuality,
  AgentEventsEnum,
} from "@heygen/liveavatar-web-sdk";

export function useSessionState() {
  const sessionRef = useRef(null);

  // 🔐 Internal gates (VERY IMPORTANT)
  const connectedRef = useRef(false);
  const streamReadyRef = useRef(false);

  const [sessionState, setSessionState] = useState(SessionState.INACTIVE);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [connectionQuality, setConnectionQuality] = useState(
    ConnectionQuality.UNKNOWN
  );
  const [isAvatarTalking, setIsAvatarTalking] = useState(false);

  // -------------------------------
  // START SESSION
  // -------------------------------
  const startSession = async (sessionToken) => {
    if (sessionRef.current) return;

    const session = new LiveAvatarSession(sessionToken, {
      apiUrl: "https://api.liveavatar.com",
      textChat: true,
    });

   

    // -------------------------------
    // SESSION STATE
    // -------------------------------
    session.on(SessionEvent.SESSION_STATE_CHANGED, (state) => {
      console.log("SDK STATE:", state);
      setSessionState(state);

      if (state === SessionState.CONNECTED) {
        connectedRef.current = true;

        // 🔥 STREAM_READY may have arrived earlier
        // if (streamReadyRef.current) {
        //   console.log("🔓 CONNECTED → releasing STREAM_READY");
        //   setIsStreamReady(true);
        // }
      }

      if (state === SessionState.DISCONNECTED) {
        connectedRef.current = false;
        streamReadyRef.current = false;
        setIsStreamReady(false);
      }
    });

    // -------------------------------
    // STREAM READY (GATED)
    // -------------------------------
    session.on(SessionEvent.SESSION_STREAM_READY, () => {
      console.log("SDK STREAM READY (event)");

      streamReadyRef.current = true;
      setIsStreamReady(true);

      // // ✅ Only release when CONNECTED
      // if (connectedRef.current) {
      //   console.log("🔓 STREAM_READY allowed");
        
      // } else {
      //   console.log("⏸ STREAM_READY waiting for CONNECTED");
      // }
    });

    // -------------------------------
    // AVATAR TALKING
    // -------------------------------
    session.on(AgentEventsEnum.AVATAR_SPEAK_STARTED, () => {
      setIsAvatarTalking(true);
    });

    session.on(AgentEventsEnum.AVATAR_SPEAK_ENDED, () => {
      setIsAvatarTalking(false);
    });

    // -------------------------------
    // CONNECTION QUALITY
    // -------------------------------
    session.on(
      SessionEvent.SESSION_CONNECTION_QUALITY_CHANGED,
      (quality) => {
        setConnectionQuality(quality);
      }
    );


      sessionRef.current = session;
    await session.start();

   
  };

  // -------------------------------
  // STOP SESSION
  // -------------------------------
  const stopSession = async () => {
    if (!sessionRef.current) return;

    try {
      await sessionRef.current.stop();
    } catch (e) {
      console.error(e);
    } finally {
      sessionRef.current = null;

      // 🔥 RESET EVERYTHING
      connectedRef.current = false;
      streamReadyRef.current = false;

      setIsStreamReady(false);
      setSessionState(SessionState.INACTIVE);
    }
  };

  // -------------------------------
  // ATTACH VIDEO ELEMENT
  // -------------------------------
  const attachElement = useCallback((element) => {
    if (!sessionRef.current || !element) return;

    // 🔐 HARD SAFETY CHECK
    if (!connectedRef.current || !streamReadyRef.current) {
      console.warn("⛔ attach() blocked — SDK not fully ready");
      return;
    }

    return sessionRef.current.attach(element);
  }, []);

  return {
    sessionState,
    isStreamReady,        // ✅ SAFE to use
    connectionQuality,
    startSession,
    stopSession,
    attachElement,
    sessionRef,
    isAvatarTalking,
  };
}
